import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { optionalIntegerString, parseIntegerString } from "../units";
import { fetchJson } from "./client";
import { immutableCache } from "./cacheClasses";
import { queryKeys } from "./queryKeys";
import { isValidBlockHeight } from "./validation";

// explorer-api serializes with JsonIgnoreCondition.WhenWritingNull, so nullable
// fields are absent (not `null`) on the wire. Model them as optional here
// so the hydrators can safely treat undefined as "no value".

interface WireMiningCostEntry {
  readonly height: string;
  readonly blockTime?: string;
  readonly feeSats: string;
  readonly mineTxCount: number;
  readonly rewardCogtoshi: string;
  readonly submissionCount: number;
  readonly gateFailures: number;
  readonly cumulativeFeeSats: string;
  readonly cumulativeRewardCogtoshi: string;
}

interface WireMiningCostSeriesResponse {
  readonly fromHeight?: string;
  readonly toHeight: string;
  readonly entries: readonly WireMiningCostEntry[];
}

// One per-block row of the network-wide mining-cost series. The two
// integer legs (`feeSats` spent mining, `rewardCogtoshi` minted) plus
// the activity counts are everything the floor is built from; the
// BTC→USD rate and any off-chain cost model are UI-supplied at render
// time (see `src/economics/miningFloor`). The API never sees fiat.
export interface NetworkMiningCostEntry {
  readonly height: bigint;
  readonly blockTime: bigint | null;
  readonly feeSats: bigint;
  readonly mineTxCount: number;
  readonly rewardCogtoshi: bigint;
  readonly submissionCount: number;
  readonly gateFailures: number;
  readonly cumulativeFeeSats: bigint;
  readonly cumulativeRewardCogtoshi: bigint;
}

export interface NetworkMiningCostSeries {
  readonly fromHeight: bigint | null;
  readonly toHeight: bigint;
  readonly entries: readonly NetworkMiningCostEntry[];
}

export function hydrateMiningCostSeries(
  wire: WireMiningCostSeriesResponse,
): NetworkMiningCostSeries {
  return {
    fromHeight: optionalIntegerString(wire.fromHeight),
    toHeight: parseIntegerString(wire.toHeight),
    entries: wire.entries.map((e) => ({
      height: parseIntegerString(e.height),
      blockTime: optionalIntegerString(e.blockTime),
      feeSats: parseIntegerString(e.feeSats),
      mineTxCount: e.mineTxCount,
      rewardCogtoshi: parseIntegerString(e.rewardCogtoshi),
      submissionCount: e.submissionCount,
      gateFailures: e.gateFailures,
      cumulativeFeeSats: parseIntegerString(e.cumulativeFeeSats),
      cumulativeRewardCogtoshi: parseIntegerString(e.cumulativeRewardCogtoshi),
    })),
  };
}

export function useNetworkMiningCostSeries(
  toHeight: number | null,
  fromHeight: number | null = null,
): UseQueryResult<NetworkMiningCostSeries> {
  return useQuery({
    queryKey: queryKeys.network.miningCostSeries(toHeight ?? -1, fromHeight),
    queryFn: async () => {
      const base = `/api/v1/network/mining-cost/series/${(toHeight ?? 0).toString()}`;
      const path =
        fromHeight === null ? base : `${base}?fromHeight=${fromHeight.toString()}`;
      return hydrateMiningCostSeries(
        await fetchJson<WireMiningCostSeriesResponse>(path),
      );
    },
    enabled: isValidBlockHeight(toHeight),
    ...immutableCache,
  });
}
